import type {
  CompositionAutocompleteOption,
  CompositionItem,
} from "@/types/mel-components";

const STORAGE_KEY = "mel.recentCompositions.v1";
const SETS_STORAGE_KEY = "mel.recentCompositionSets.v1";
const MAX_RECENT = 20;
const MAX_RECENT_SETS = 5;

export interface RecentCompositionSet {
  label: string;
  items: Array<Pick<CompositionItem, "compositionId" | "compositionName" | "percentage">>;
}

function readAll(): CompositionAutocompleteOption[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item: unknown) => {
        const row = item as { id?: unknown; label?: unknown };
        const id = Number(row?.id) || 0;
        const label = String(row?.label || "").trim();
        if (!id || !label) return null;
        return { id, label };
      })
      .filter((item): item is CompositionAutocompleteOption => item !== null);
  } catch {
    return [];
  }
}

function writeAll(items: CompositionAutocompleteOption[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_RECENT)));
  } catch {
    // ignore (private mode, quota, etc.)
  }
}

/** Compositions récemment sélectionnées (les plus récentes en premier). */
export function getRecentCompositions(): CompositionAutocompleteOption[] {
  return readAll();
}

/** Enregistre une composition utilisée (déplacée en tête, dédoublonnée par id). */
export function rememberComposition(composition: CompositionAutocompleteOption) {
  const id = Number(composition?.id) || 0;
  const label = String(composition?.label || "").trim();
  if (!id || !label) return;

  const next = [
    { id, label },
    ...readAll().filter((item) => item.id !== id),
  ].slice(0, MAX_RECENT);

  writeAll(next);
}

/**
 * Filtre l’historique pour l’autocomplete.
 * - `term` vide → tous les récents (hors exclus)
 * - sinon → libellés contenant le terme (insensible à la casse)
 */
export function filterRecentCompositions(
  term: string,
  excludeIds: Iterable<number> = [],
): CompositionAutocompleteOption[] {
  const excluded = new Set(
    Array.from(excludeIds)
      .map((id) => Number(id))
      .filter((id) => id > 0),
  );
  const needle = term.trim().toLowerCase();

  return getRecentCompositions().filter((item) => {
    if (excluded.has(item.id)) return false;
    if (!needle) return true;
    return item.label.toLowerCase().includes(needle);
  });
}

function compositionSetLabel(items: RecentCompositionSet["items"]): string {
  return items
    .map(
      (item) =>
        `${item.compositionName} ${Number(item.percentage).toLocaleString(
          "fr-FR",
          { maximumFractionDigits: 2 },
        )} %`,
    )
    .join(", ");
}

/** Compositions complètes récentes, prêtes à être réappliquées. */
export function getRecentCompositionSets(): RecentCompositionSet[] {
  try {
    const raw = localStorage.getItem(SETS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((value: unknown) => {
      const row = value as { items?: unknown };
      if (!Array.isArray(row?.items) || row.items.length === 0) return [];
      const items = row.items.flatMap((value: unknown) => {
        const item = value as Record<string, unknown>;
        const compositionId = Number(item.compositionId) || 0;
        const compositionName = String(item.compositionName || "").trim();
        const percentage = Number(item.percentage) || 0;
        return compositionId && compositionName && percentage > 0
          ? [{ compositionId, compositionName, percentage }]
          : [];
      });
      if (items.length !== row.items.length) return [];
      return [{ label: compositionSetLabel(items), items }];
    });
  } catch {
    return [];
  }
}

/** Mémorise un mélange complet, dédoublonné par matériaux et pourcentages. */
export function rememberCompositionSet(items: CompositionItem[]) {
  const normalized = items.map((item) => ({
    compositionId: Number(item.compositionId),
    compositionName: item.compositionName.trim(),
    percentage: Number(item.percentage),
  }));
  if (
    !normalized.length ||
    normalized.some(
      (item) =>
        !item.compositionId || !item.compositionName || item.percentage <= 0,
    )
  ) {
    return;
  }

  const signature = JSON.stringify(normalized);
  const next = [
    { label: compositionSetLabel(normalized), items: normalized },
    ...getRecentCompositionSets().filter(
      (set) => JSON.stringify(set.items) !== signature,
    ),
  ].slice(0, MAX_RECENT_SETS);

  try {
    localStorage.setItem(SETS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore (private mode, quota, etc.)
  }
}
